import React, { useState, useEffect, useContext } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';

// Fisher-Yates (aka Knuth) Shuffle Algorithm
function shuffleArray(array) {
  let currentIndex = array.length,  randomIndex;
  const newArray = [...array]; // Clone to avoid mutating the original array passed in

  // While there remain elements to shuffle.
  while (currentIndex !== 0) {

    // Pick a remaining element.
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [newArray[currentIndex], newArray[randomIndex]] = [
      newArray[randomIndex], newArray[currentIndex]];
  }
  return newArray;
}

const QuizAttemptPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const adHocKey = `adHoc_${id}`;
  const quizStateKey = `quiz_state_${id}`;

  // Add submitting state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine ad-hoc questions and meta, shuffling if new from state
  let adHocQuestionsForQuiz;
  let adHocMetaForQuiz;
  const stateQ = location.state?.questions;
  const stateMeta = location.state?.meta;
  const storedJSON = localStorage.getItem(adHocKey);

  if (stateQ) { // Fresh ad-hoc quiz from location.state
    // Do NOT shuffle past questions from the bank - preserve their original order
    adHocQuestionsForQuiz = stateQ;
    adHocMetaForQuiz = stateMeta || {};
    localStorage.setItem(adHocKey, JSON.stringify({ questions: adHocQuestionsForQuiz, meta: adHocMetaForQuiz }));
    // Clear old quiz state when starting with new meta settings
    localStorage.removeItem(quizStateKey);
  } else if (storedJSON) { // Resuming an ad-hoc quiz from localStorage
    const storedData = JSON.parse(storedJSON);
    adHocQuestionsForQuiz = storedData.questions; // Bank questions maintain their original order
    adHocMetaForQuiz = storedData.meta || {};
  } else {
    adHocQuestionsForQuiz = null;
    adHocMetaForQuiz = {};
  }

  // Initialize state from localStorage if available
  const storedState = localStorage.getItem(quizStateKey);
const parsedState = storedState ? JSON.parse(storedState) : null;
const initialQuiz = parsedState ? ({ ...parsedState.quiz, questions: parsedState.quiz.questions || parsedState.quiz.Questions }) : null;
  const [quiz, setQuiz] = useState(initialQuiz);
  const [answers, setAnswers] = useState(parsedState ? parsedState.answers : {});
  const [timeLeft, setTimeLeft] = useState(parsedState ? parsedState.timeLeft : null);
  const [sections, setSections] = useState(parsedState ? parsedState.sections : []);
  const [activeSection, setActiveSection] = useState(parsedState ? parsedState.activeSection : 0);
  const [sectionTimeLeft, setSectionTimeLeft] = useState(parsedState ? parsedState.sectionTimeLeft : null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(parsedState ? parsedState.currentPage : 1);
  const [questionsPerPage, setQuestionsPerPage] = useState(parsedState ? parsedState.questionsPerPage : null);
  const [currentAttempt, setCurrentAttempt] = useState(0);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (quiz && !result) {
      const stateToStore = {
        quiz,
        answers,
        timeLeft,
        sections,
        activeSection,
        sectionTimeLeft,
        currentPage,
        questionsPerPage
      };
      localStorage.setItem(quizStateKey, JSON.stringify(stateToStore));
    }
  }, [quiz, answers, timeLeft, sections, activeSection, sectionTimeLeft, currentPage, questionsPerPage, result]);

  // Clear localStorage when quiz is completed
  useEffect(() => {
    if (result) {
      localStorage.removeItem(quizStateKey);
      localStorage.removeItem(adHocKey);
      
      // Clear Question Bank settings when quiz completes
      if (adHocQuestionsForQuiz) {
        localStorage.removeItem('questionBank_questionsPerPage');
        localStorage.removeItem('questionBank_timeLimit');
      }
      
      // Clean up timer keys based on resetTimerOnAttempt setting
      if (quiz?.resetTimerOnAttempt !== false) {
        localStorage.removeItem(`adHoc_timer_start_${id}_attempt_${currentAttempt}`);
        localStorage.removeItem(`regular_quiz_timer_start_${id}_attempt_${currentAttempt}`);
        // Clean up section timers for current attempt
        for (let i = 0; i < 10; i++) { // Assume max 10 sections
          localStorage.removeItem(`section_timer_${id}_${i}_attempt_${currentAttempt}`);
        }
      } else {
        localStorage.removeItem(`adHoc_timer_start_${id}`);
        localStorage.removeItem(`regular_quiz_timer_start_${id}`);
        // Clean up section timers without attempt
        for (let i = 0; i < 10; i++) { // Assume max 10 sections
          localStorage.removeItem(`section_timer_${id}_${i}`);
        }
      }
      localStorage.removeItem(`shuffle_regular_${id}`);
    }
  }, [result, id, currentAttempt, quiz]);

  // Fetch current attempt count for timer reset
  useEffect(() => {
    if (id && !adHocQuestionsForQuiz) {
      api.get(`/submissions/quiz/${id}/attempts`)
        .then(res => {
          setCurrentAttempt(res.data.currentAttempts);
        })
        .catch(err => {
          console.error('Failed to fetch attempt count:', err);
          setCurrentAttempt(0); // Default to 0 if fetch fails
        });
    }
  }, [id, adHocQuestionsForQuiz]);

  useEffect(() => {
    setError(null); // Reset error on each attempt to load/setup quiz

    if (adHocQuestionsForQuiz) {
      const timeLimit = adHocMetaForQuiz.timeLimit || null;
      const quizData = { questions: adHocQuestionsForQuiz, title: `Examination`, timeLimit };
      setQuiz(quizData);
      // Use questionsPerPage from meta if set, otherwise show all questions
      const qpp = adHocMetaForQuiz.questionsPerPage;
      setQuestionsPerPage(qpp && qpp > 0 ? qpp : null);
      
      const adHocTimerKey = quiz?.resetTimerOnAttempt !== false 
        ? `adHoc_timer_start_${id}_attempt_${currentAttempt}`
        : `adHoc_timer_start_${id}`;
      if (quizData.timeLimit) {
        let start = parseInt(localStorage.getItem(adHocTimerKey), 10);
        if (isNaN(start) || !start) { 
          start = Date.now(); 
          localStorage.setItem(adHocTimerKey, start.toString()); 
        }
        const end = start + quizData.timeLimit * 60 * 1000;

        if (Date.now() >= end) {
          setTimeLeft(0);
          if (!result) { handleSubmit(); }
        } else {
          setTimeLeft(Math.max(0, Math.ceil((end - Date.now())/1000)));
          const timer = setInterval(() => {
            const tl = Math.ceil((end - Date.now())/1000);
            if (tl <= 0) { 
              clearInterval(timer); 
              if (!result) { handleSubmit(); }
            } else {
              setTimeLeft(tl);
            }
          }, 1000);
          return () => clearInterval(timer);
        }
      }
    } else if (id) {
      api.get(`/quizzes/${id}`)
        .then(res => {
          const quizData = res.data;
          // Shuffle questions once per quiz
          const shuffleKey = `shuffle_regular_${id}`;
          const storedShuffle = localStorage.getItem(shuffleKey);
          if (!storedShuffle) {
            const shuffled = shuffleArray(quizData.questions);
            localStorage.setItem(shuffleKey, JSON.stringify(shuffled));
            quizData.questions = shuffled;
          } else {
            quizData.questions = JSON.parse(storedShuffle);
          }
          setQuiz(quizData);
          // Use questionsPerPage from quiz if set, otherwise show all questions
          const qpp = quizData.questionsPerPage;
          setQuestionsPerPage(qpp && qpp > 0 ? qpp : null);
          if (quizData.timedSections?.length) setSections(quizData.timedSections);
          
          const regularQuizTimerKey = quizData.resetTimerOnAttempt !== false 
            ? `regular_quiz_timer_start_${id}_attempt_${currentAttempt}`
            : `regular_quiz_timer_start_${id}`;
          if (quizData.timeLimit && !(quizData.timedSections?.length)) {
            let start = parseInt(localStorage.getItem(regularQuizTimerKey), 10);
            if (isNaN(start) || !start) { 
              start = Date.now(); 
              localStorage.setItem(regularQuizTimerKey, start.toString()); 
            }
            const end = start + quizData.timeLimit * 60 * 1000;
            
            const update = () => {
              const tl = Math.floor((end - Date.now()) / 1000);
              if (tl <= 0) { 
                setTimeLeft(0); 
                if (!result) { handleSubmit(); }
              } else {
                setTimeLeft(tl);
              }
            };
            update(); // Initial call
            if (Date.now() < end) {
              const timer = setInterval(update, 1000);
              return () => clearInterval(timer);
            } else {
              if (!result) { handleSubmit(); }
            }
          }
        })
        .catch(err => {
          console.error(err);
          setError(err.response?.data?.error || 'Failed to load quiz. Please check the URL or try again later.');
        });
    }
  }, [id, location.key, result, currentAttempt]);

  // Section timer with persistence
  useEffect(() => {
    if (sections.length > 0) {
      const sectionTimerKey = quiz?.resetTimerOnAttempt !== false 
        ? `section_timer_${id}_${activeSection}_attempt_${currentAttempt}`
        : `section_timer_${id}_${activeSection}`;
      let start = parseInt(localStorage.getItem(sectionTimerKey), 10);
      if (isNaN(start) || !start) {
        start = Date.now();
        localStorage.setItem(sectionTimerKey, start.toString());
      }
      
      const limitSec = sections[activeSection].limit * 60;
      const deadline = start + limitSec * 1000;
      
      const update = () => {
        const tl = Math.floor((deadline - Date.now()) / 1000);
        if (tl <= 0) {
          if (activeSection < sections.length - 1) {
            setActiveSection(s => s + 1);
          } else {
            handleSubmit();
          }
        } else {
          setSectionTimeLeft(tl);
        }
      };
      
      update(); // Initial call
      const secTimer = setInterval(update, 1000);
      return () => {
        clearInterval(secTimer);
        // Don't remove timer from localStorage - let it persist across navigation
        // It will be cleaned up only when quiz is submitted (in the result useEffect)
      };
    }
  }, [sections, activeSection, currentAttempt]);

  const handleSelect = (qid, opt) => {
    setAnswers(prev => {
      const newAnswers = { ...prev, [qid]: opt };
      return newAnswers;
    });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent multiple submissions
    
    setIsSubmitting(true); // Set submitting state
    const submission = Object.entries(answers).map(([questionId, answer]) => ({ questionId: Number(questionId), answer }));
    
    try {
      if (adHocQuestionsForQuiz) {
        // Grade locally
        const total = adHocQuestionsForQuiz.length;
        const correct = adHocQuestionsForQuiz.filter(q => answers[q.id] === q.answer).length;
        const score = (correct / total) * 100;
        setResult({ score, submission, correct, total });
      } else {
        const payload = { answers: submission };
        const res = await api.post(`/submissions/${id}/submit`, payload);
        setResult(res.data);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Submission failed');
      setIsSubmitting(false); // Reset submitting state on error
    }
  };

  const handleReset = () => {
    localStorage.removeItem(adHocKey);
    localStorage.removeItem(quizStateKey);
    
    // Clean up timer keys based on resetTimerOnAttempt setting
    if (quiz?.resetTimerOnAttempt !== false) {
      localStorage.removeItem(`adHoc_timer_start_${id}_attempt_${currentAttempt}`);
      localStorage.removeItem(`regular_quiz_timer_start_${id}_attempt_${currentAttempt}`);
      // Clean up section timers for current attempt
      for (let i = 0; i < 10; i++) { // Assume max 10 sections
        localStorage.removeItem(`section_timer_${id}_${i}_attempt_${currentAttempt}`);
      }
    } else {
      localStorage.removeItem(`adHoc_timer_start_${id}`);
      localStorage.removeItem(`regular_quiz_timer_start_${id}`);
      // Clean up section timers without attempt
      for (let i = 0; i < 10; i++) { // Assume max 10 sections
        localStorage.removeItem(`section_timer_${id}_${i}`);
      }
    }
    
    // Navigate based on user role
    if (user?.role === 'teacher') {
      navigate('/teachers/dashboard/quizzes');
    } else if (user?.role === 'admin') {
      navigate('/admin/dashboard/quizzes');
    } else {
      navigate('/students/dashboard/past-questions');
    }
  };

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4 sm:p-6">
      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 text-center max-w-md w-full">
        <div className="text-6xl mb-4">⚠️</div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Quiz Error</h2>
        <p className="text-sm sm:text-base text-red-600 mb-6">{error}</p>
        <button 
          onClick={() => navigate(-1)}
          className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300"
        >
          Go Back
        </button>
      </div>
    </div>
  );
  
  if (!quiz) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4 sm:p-6">
      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 text-center w-full max-w-md">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Loading Quiz</h2>
        <p className="text-sm sm:text-base text-gray-600">Please wait while we prepare your quiz...</p>
      </div>
    </div>
  );
  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Results Header */}
          <div className="bg-white rounded-xl shadow-lg p-5 sm:p-8 mb-6 sm:mb-8 text-center">
            <div className="text-5xl sm:text-6xl mb-4">🎉</div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Quiz Completed!</h1>
            <h2 className="text-base sm:text-xl text-gray-700 mb-6">{quiz.title}</h2>
            
            {/* Score Display */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl p-4 sm:p-6 mb-6">
              <div className="text-3xl sm:text-4xl font-bold mb-2">
                {result.correct !== undefined ? result.correct : 
                 quiz.questions.filter(q => {
                   const subs = Array.isArray(result.submission)
                     ? result.submission
                     : result.submission.answers;
                   return subs.find(a => Number(a.questionId) === q.id)?.answer === q.answer;
                 }).length} / {result.total !== undefined ? result.total : quiz.questions.length}
              </div>
              <p className="text-sm sm:text-lg opacity-90">Questions Correct</p>
              <div className="text-lg sm:text-2xl font-semibold mt-2">
                Score: {result.correct !== undefined ? result.correct : 
                        quiz.questions.filter(q => {
                          const subs = Array.isArray(result.submission)
                            ? result.submission
                            : result.submission.answers;
                          return subs.find(a => Number(a.questionId) === q.id)?.answer === q.answer;
                        }).length} out of {result.total !== undefined ? result.total : quiz.questions.length}
              </div>
            </div>
            
            <button 
              onClick={() => navigate(-1)} 
              className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Back to Quizzes
            </button>
          </div>

          {/* Detailed Results */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-2xl sm:text-3xl mr-3">📋</span>
              Detailed Results
            </h3>
            
            <div className="space-y-6">
              {(() => {
                // Use the same shuffled order from the quiz attempt
                // For ad-hoc quizzes, questions are already in the correct order
                // For regular quizzes, retrieve the shuffled order from localStorage
                let questionsToDisplay = quiz.questions;
                
                if (!adHocQuestionsForQuiz && id) {
                  const shuffleKey = `shuffle_regular_${id}`;
                  const storedShuffle = localStorage.getItem(shuffleKey);
                  if (storedShuffle) {
                    questionsToDisplay = JSON.parse(storedShuffle);
                  }
                }
                
                return questionsToDisplay;
              })().map((q, index) => {
                const submissions = Array.isArray(result.submission)
                  ? result.submission
                  : result.submission.answers;
                const userAns = submissions.find(a => Number(a.questionId) === q.id)?.answer;
                const isCorrect = userAns === q.answer;
                
                return (
                  <div key={q.id} className={`border-l-4 ${isCorrect ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'} p-4 sm:p-6 rounded-r-xl`}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                      <h4 className="font-semibold text-base sm:text-lg text-gray-900 flex items-center">
                        <span className="bg-gray-200 text-gray-700 rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold mr-3">
                          {index + 1}
                        </span>
                        Question {index + 1}
                      </h4>
                      <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium w-fit ${
                        isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                      </span>
                    </div>
                    
                    <div className="mb-4 p-4 bg-white rounded-lg">
                      {/* Display question images if they exist */}
                      {(() => {
                        // Parse images if it's a string, otherwise use as-is
                        let images = q.images;
                        if (typeof images === 'string') {
                          try {
                            images = JSON.parse(images);
                          } catch (e) {
                            images = [];
                          }
                        }
                        // Ensure it's an array
                        if (!Array.isArray(images)) {
                          images = [];
                        }
                        
                        return images.length > 0 && (
                          <div className="mb-4 flex flex-wrap gap-2">
                            {images.map((imgSrc, imgIdx) => (
                              <img 
                                key={imgIdx}
                                src={imgSrc} 
                                alt={`Question ${index + 1} visual content ${imgIdx + 1}`}
                                className="max-w-full max-h-64 object-contain border rounded shadow-sm"
                              />
                            ))}
                          </div>
                        );
                      })()}
                      <p className="text-sm sm:text-base text-gray-800 font-medium">{q.text}</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-white rounded-lg">
                        <p className="text-sm font-medium text-gray-600 mb-2">Your Answer:</p>
                        <p className={`font-semibold ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                          {userAns || 'No answer provided'}
                        </p>
                      </div>
                      
                      <div className="p-4 bg-white rounded-lg">
                        <p className="text-sm font-medium text-gray-600 mb-2">Correct Answer:</p>
                        <p className="font-semibold text-blue-600">{q.answer}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0">
              <button 
                onClick={() => navigate(-1)} 
                className="text-blue-500 hover:text-blue-700 font-medium transition-colors duration-200 flex items-center gap-2 text-sm sm:text-base shrink-0"
              >
                <span className="text-lg sm:text-xl">←</span>
                <span>Back</span>
              </button>
              <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
                  <span className="text-2xl sm:text-3xl">📝</span>
                  <span className="truncate">{quiz.title}</span>
                </h1>
                {quiz.course && <p className="text-sm sm:text-base text-gray-600 mt-1">Course: {quiz.course.title}</p>}
                {adHocQuestionsForQuiz && adHocMetaForQuiz && (adHocMetaForQuiz.year || adHocMetaForQuiz.subject || adHocMetaForQuiz.section) && (
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    {adHocMetaForQuiz.year && `Year: ${adHocMetaForQuiz.year}`}
                    {adHocMetaForQuiz.subject && ` | Subject: ${adHocMetaForQuiz.subject}`}
                    {adHocMetaForQuiz.section && ` | Section: ${adHocMetaForQuiz.section}`}
                  </p>
                )}
              </div>
            </div>

            {/* Timer Display */}
            <div className="flex items-center">
              {sections.length > 0 ? (
                sectionTimeLeft !== null && (
                  <div className="bg-orange-100 text-orange-800 px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center gap-2 w-fit">
                    <span className="text-lg sm:text-xl">⏱️</span>
                    <div>
                      <div className="text-xs sm:text-sm">Section {activeSection+1}/{sections.length}</div>
                      <div className="font-bold text-sm sm:text-base">{Math.floor(sectionTimeLeft/60)}:{String(sectionTimeLeft%60).padStart(2,'0')}</div>
                    </div>
                  </div>
                )
              ) : timeLeft !== null && (
                <div className="bg-red-100 text-red-800 px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center gap-2 w-fit">
                  <span className="text-lg sm:text-xl">⏰</span>
                  <div>
                    <div className="text-xs sm:text-sm">Time Remaining</div>
                    <div className="font-bold text-sm sm:text-base">{Math.floor(timeLeft/60)}:{String(timeLeft%60).padStart(2,'0')}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
        {/* Section Navigation */}
        {sections.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Section Navigation</h2>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <button 
                disabled={activeSection===0} 
                onClick={()=>setActiveSection(s=>s-1)} 
                className="w-full sm:w-auto px-4 py-2 bg-gray-500 text-white rounded-lg font-medium disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors duration-200"
              >
                ← Previous Section
              </button>
              
              <div className="text-center">
                <div className="text-sm text-gray-600">Current Section</div>
                <div className="text-lg font-bold text-gray-900">{activeSection + 1} of {sections.length}</div>
              </div>
              
              <button 
                disabled={activeSection===sections.length-1} 
                onClick={()=>setActiveSection(s=>s+1)} 
                className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-lg font-medium disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600 transition-colors duration-200"
              >
                Next Section →
              </button>
            </div>
          </div>
        )}

        {/* Questions */}
        <div className="space-y-6">
          {(() => {
            let list = sections.length > 0
              ? quiz.questions.slice(sections[activeSection].start, sections[activeSection].end + 1)
              : quiz.questions;
            if (!sections.length && questionsPerPage) {
              const start = (currentPage - 1) * questionsPerPage;
              list = list.slice(start, start + questionsPerPage);
            }
            return list;
          })().map((q, index) => {
            const globalIndex = sections.length > 0 
              ? sections[activeSection].start + index + 1 
              : (!sections.length && questionsPerPage) 
                ? (currentPage - 1) * questionsPerPage + index + 1 
                : index + 1;
                
            // Parse options if they are stored as a JSON string
            let questionOptions = [];
            try {
              questionOptions = typeof q.options === 'string' 
                ? JSON.parse(q.options) 
                : (Array.isArray(q.options) ? q.options : []);
            } catch (err) {
              console.error('Error parsing options for question', q.id, err);
              questionOptions = [];
            }
            
            return (
              <div key={q.id} className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start gap-3 sm:gap-4 mb-6">
                  <div className="flex-shrink-0">
                    <div className="bg-blue-500 text-white rounded-full w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center font-bold text-sm sm:text-lg">
                      {globalIndex}
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Question {globalIndex}</h3>
                    {/* Display question images if they exist */}
                    {(() => {
                      // Parse images if it's a string, otherwise use as-is
                      let images = q.images;
                      if (typeof images === 'string') {
                        try {
                          images = JSON.parse(images);
                        } catch (e) {
                          images = [];
                        }
                      }
                      // Ensure it's an array
                      if (!Array.isArray(images)) {
                        images = [];
                      }
                      
                      return images.length > 0 && (
                        <div className="mb-4 flex flex-wrap gap-2">
                          {images.map((imgSrc, imgIdx) => (
                            <img 
                              key={imgIdx}
                              src={imgSrc} 
                              alt={`Question ${globalIndex} visual content ${imgIdx + 1}`}
                                className="w-full sm:w-auto max-w-full max-h-64 object-contain border rounded shadow-sm"
                            />
                          ))}
                        </div>
                      );
                    })()}
                      <p className="text-sm sm:text-base text-gray-800 leading-relaxed">{q.text}</p>
                  </div>
                </div>
                
                {questionOptions && questionOptions.length > 0 && (
                    <div className="space-y-3 ml-0 sm:ml-14">
                    {questionOptions.map((opt, i) => {
                      const isSelected = answers[q.id] === opt;
                      return (
                          <label key={i} className={`flex items-start sm:items-center p-3 rounded-lg cursor-pointer transition-all duration-200 gap-2 ${
                          isSelected 
                            ? 'bg-blue-100 border-2 border-blue-500' 
                            : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                        }`}>
                          <input
                            type="radio"
                            name={`q_${q.id}`}
                            value={opt}
                            checked={isSelected}
                            onChange={() => handleSelect(q.id, opt)}
                            className="sr-only"
                          />
                          <div className={`w-5 h-5 rounded-full border-2 mt-0.5 sm:mt-0 shrink-0 flex items-center justify-center ${
                            isSelected 
                              ? 'border-blue-500 bg-blue-500' 
                              : 'border-gray-300'
                          }`}>
                            {isSelected && <div className="w-2 h-2 bg-white rounded-full"></div>}
                          </div>
                          <span className={`text-sm sm:text-base text-gray-800 ${isSelected ? 'font-medium' : ''}`}>{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pagination for non-section quizzes */}
        {!sections.length && questionsPerPage && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mt-6 sm:mt-8">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
              <button 
                disabled={currentPage===1} 
                onClick={()=>setCurrentPage(p=>p-1)} 
                className="w-full sm:w-auto px-4 py-2 bg-gray-500 text-white rounded-lg font-medium disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors duration-200"
              >
                ← Previous
              </button>
              
              <div className="text-center">
                <div className="text-sm text-gray-600">Page</div>
                <div className="text-lg font-bold text-gray-900">{currentPage} of {Math.ceil(quiz.questions.length/questionsPerPage)}</div>
              </div>
              
              <button 
                disabled={currentPage>=Math.ceil(quiz.questions.length/questionsPerPage)} 
                onClick={()=>setCurrentPage(p=>p+1)} 
                className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-lg font-medium disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-600 transition-colors duration-200"
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* Submit Section */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mt-6 sm:mt-8">
          <div className="text-center">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Ready to Submit?</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-6">
              Make sure you&apos;ve answered all questions before submitting your quiz.
            </p>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
              <button 
                onClick={handleSubmit} 
                disabled={isSubmitting}
                className={`w-full sm:w-auto px-6 sm:px-8 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2 ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                <span className="text-xl">{isSubmitting ? '⏳' : '✅'}</span>
                <span>{isSubmitting ? 'Submitting...' : 'Submit Quiz'}</span>
              </button>

              {adHocQuestionsForQuiz && (
                <button 
                  onClick={handleReset} 
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto px-6 py-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                    isSubmitting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-red-500 hover:bg-red-600 text-white'
                  }`}
                >
                  <span className="text-xl">🔄</span>
                  <span>Reset Quiz</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizAttemptPage;
